<script setup lang="ts">
import type { SubjectCode } from '~/models/Subject'
import type { GradeCode } from '~/models/Grade'
import type { ContentToolbox } from '~/models/Content/ContentToolbox'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { findRelevantGradeType } from '~/utils/gradeSorter'
import useArrayUtils from '~/utils/arrayUtils'
import useProductStore from '~/stores/product'
import { GradeType } from '~/models/GradeType'
import { ContentType } from '~/models/Content/ContentType'
import { ColorTheme } from '~/models/Content/Color'
import useSearchHelper from '~/composables/useSearchHelper'
import { useColorTheme } from '~/composables/useColorTheme'
import useCardStyles from '~/composables/useCardStyles'
import useContentApi from '~/api/contentApi'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import ToolboxPrimaryCard from '~/components/cards/ToolboxPrimaryCard.vue'
import ProductGenericCard from '~/components/cards/ProductGenericCard.vue'

const {
  subject,
  grade
} = defineProps<{
  subject: SubjectCode
  grade: GradeCode
}>()

const { findProductsByGradeAndSubject } = useProductStore()
const { theme } = useColorTheme(ColorTheme.GreenSeagreen)
const { defaultContentTypes } = useSearchHelper()
const { themeStyle } = useCardStyles(theme)
const { findContents } = useContentApi()
const { truthy } = useArrayUtils()

const gradeType = computed(() => findRelevantGradeType(grade))
const isSubject8to10 = computed(() => gradeType.value === GradeType.Grade8To10)
const isSubject1to4 = computed(() => gradeType.value === GradeType.Grade1To4)
const locationIds = computed(() => findProductsByGradeAndSubject(grade, subject)
  .map(product => product.aunivers.locationId)
  .filter(truthy<number>)
)

const { data, isSuccess } = useQuery({
  queryKey: computed(() => ['subject_toolbox', locationIds.value]),
  queryFn: async () => (await findContents<ContentToolbox>({
    parentLocationIdCriterion: locationIds.value,
    contentTypeCriterion: [ContentType.Toolbox],
  }))[0],
  staleTime: Infinity,
  enabled: computed(() => locationIds.value.length > 0),
})

const { data: resources, isLoading } = useQuery({
  queryKey: computed(() => ['subject_toolbox_resources', data.value?.locationId]),
  queryFn: () => findContents({
    contentTypeCriterion: [...defaultContentTypes],
    parentLocationIdCriterion: [Number(data.value?.locationId)],
    sortField: data.value?.sortField,
    sortOrder: data.value?.sortOrder,
  }),
  staleTime: Infinity,
  enabled: computed(() => isSuccess.value && !!data.value?.locationId),
})
</script>

<template>
  <section
    v-if="data"
    class="bg-green-40 mt-32 py-12 text-white"
    :class="{ 'py-8!': isSubject1to4 }"
  >
    <div class="mx-auto size-full max-w-[1084px] px-4 sm:px-8">
      <h2
        v-if="isSubject8to10"
        class="text-yellow-10 mb-6 text-2xl font-medium"
        v-text="data.title"
      />
      <ul
        v-if="!isLoading"
        :style="{
          ...themeStyle,
          gridTemplateColumns: !isSubject8to10 ? `repeat(${Math.max(1, resources?.length ?? 0)}, minmax(0, 14rem))` : '',
        }"
        class="grid w-full place-content-center items-center gap-12"
        :class="{ 'grid-cols-4 gap-6': isSubject8to10 }"
      >
        <li
          v-for="resource in resources"
          :key="resource.locationId"
          :class="{ 'grid size-full': isSubject8to10 }"
        >
          <ProductGenericCard
            v-if="isSubject8to10"
            :show-label="false"
            :resource
          />
          <ToolboxPrimaryCard v-else :resource />
        </li>
      </ul>
      <AuSkeletonWrapper v-else class="grid grid-cols-4 gap-6">
        <AuSkeleton height="200px" />
        <AuSkeleton height="200px" />
        <AuSkeleton height="200px" />
        <AuSkeleton height="200px" />
      </AuSkeletonWrapper>
    </div>
  </section>
</template>
